import { Select as BaseSelect } from '@base-ui/react/select';
import { merge } from 'lodash-es';
import * as React from 'react';

import type { VariantProp } from '../../common/colors';
import type {
  ControlAppearance,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from '../../controls/shared/types';
import type { ComboBoxRow, ComboBoxRowGroup } from '../shared/ComboBox';
import { type IconName, Icon } from '../../assets/Icon/Icon';
import { Button } from '../../controls/Button/Button';
import { getInteriorVariantStyles } from '../../controls/Button/Button.styles';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import {
  useControlAppearance,
  useControlGhost,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../../controls/shared/context';
import { iconColor, labelColor, transitions } from '../../controls/shared/styles';
import { controlArrowsSize, controlIconSize } from '../../controls/shared/vars';
import { type CSS, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { useControllableState } from '../../utilities/useControllableState';
import {
  ComboBoxGroup,
  ComboBoxGroupLabel,
  ComboBoxList,
  ComboBoxListOptionBody,
  ComboBoxListOptionRoot,
  ComboBoxPopup,
  ComboBoxPositioner,
  useComboBoxCollection,
  useComboBoxLabel,
  useValueSet,
} from '../shared/ComboBox';
import { SelectContainer, SelectTrigger } from './SelectTrigger';

const SelectIcon = styled(Icon, {
  alignSelf: 'center',
  size: controlIconSize,
  color: iconColor,
});
SelectIcon.displayName = 'SelectIcon';

export const SelectValue = styled(Text, {
  width: '100%',
  color: labelColor,
  textAlign: 'left',
  truncate: true,
  transition: transitions.control,

  variants: {
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      small: {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
      large: {
        ...BodySansSizes,
      },
      'x-large': {
        ...LargeSansSizes,
      },
    },
  },
});
SelectValue.displayName = 'SelectValue';

const SelectArrows = styled(Icon, {
  alignSelf: 'center',
  size: controlArrowsSize,
  color: iconColor,
});
SelectArrows.displayName = 'SelectArrows';

// The shared combobox chrome, composed onto Base UI's select parts.
const SelectPositioner = styled(BaseSelect.Positioner, ComboBoxPositioner);
SelectPositioner.displayName = 'SelectPositioner';

const SelectPopover = styled(BaseSelect.Popup, ComboBoxPopup, {
  // Only as wide as the content needs, but at least the trigger's width; beyond 400px labels
  // should wrap rather than stretch the popup across the viewport.
  maxWidth: 'max(var(--anchor-width), 400px)',
});
SelectPopover.displayName = 'SelectPopover';

const SelectList = styled(BaseSelect.List, ComboBoxList);
SelectList.displayName = 'SelectList';

const SelectGroup = styled(BaseSelect.Group, ComboBoxGroup);
SelectGroup.displayName = 'SelectGroup';

const SelectGroupLabel = styled(BaseSelect.GroupLabel, ComboBoxGroupLabel);
SelectGroupLabel.displayName = 'SelectGroupLabel';

const SelectOption = styled(BaseSelect.Item, ComboBoxListOptionRoot);
SelectOption.displayName = 'SelectOption';

type SelectProps<T = object> = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'children' | 'defaultValue' | 'disabled' | 'name' | 'onChange' | 'value'
> & {
  appearance?: ControlAppearance;
  canClearValue?: boolean;
  /**
   * `SelectItem` and `SelectSection` elements, or a function mapping `items` onto them.
   */
  children?: React.ReactNode | ((item: T) => React.ReactElement);
  controlSize?: ControlSize;
  /**
   * Alias of `items`.
   */
  defaultItems?: Iterable<T>;
  defaultValue?: string;
  disabled?: boolean;
  /**
   * Values that can't be selected, on top of any `SelectItem` marked `disabled`.
   */
  disabledKeys?: Iterable<string | number>;
  fixtures?: VariantProp;
  ghost?: boolean;
  icon?: IconName;
  /**
   * Shows an em-dash in place of a value, for a control standing in for a mixed selection.
   */
  indeterminate?: boolean;
  invalid?: boolean;
  /**
   * Options for a function child, in place of listing them statically.
   */
  items?: Iterable<T>;
  /**
   * Accessible name for the control and its options. Falls back to `placeholder`.
   */
  label?: React.ReactNode;
  maxWidth?: ControlMaxWidth;
  minWidth?: ControlMinWidth;
  /**
   * Submits the value with the surrounding form.
   */
  name?: string;
  placeholder?: string;
  ref?: React.Ref<HTMLButtonElement>;
  showPlaceholderForIndeterminate?: boolean;
  size?: ControlSize;
  value?: string | null;
  variant?: ControlVariantInput;
  width?: ControlWidth;
  onOpenChange?: (open: boolean) => void;
  onValueChange?: (key: string) => void;
};

export function Select<T extends object = object>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  appearance,
  canClearValue,
  children,
  controlSize: controlSizeProp,
  defaultItems,
  defaultValue,
  disabled = false,
  disabledKeys,
  fixtures,
  ghost,
  icon,
  indeterminate,
  invalid,
  items,
  label,
  maxWidth,
  minWidth,
  name,
  placeholder = 'Select...',
  ref,
  showPlaceholderForIndeterminate,
  size,
  value,
  variant,
  width,
  onOpenChange,
  onValueChange,
  ...remaining
}: SelectProps<T>) {
  const controlAppearance = useControlAppearance(appearance, 'muted');
  const controlGhost = useControlGhost(ghost, false);
  const controlSize = useControlSize(size ?? controlSizeProp, 'medium');
  const controlVariant = useControlVariant(variant, 'neutral');
  const controlWidth = useControlWidth(width) ?? 'min-content';
  const controlMaxWidth = useControlMaxWidth(maxWidth) ?? '100%';
  const controlMinWidth = useControlMinWidth(minWidth) ?? 0;

  const collection = useComboBoxCollection(children, items ?? defaultItems);
  const disabledValues = useValueSet(disabledKeys);

  const handleValueChange = React.useCallback(
    (next: string | null) => onValueChange?.(next ?? ''),
    [onValueChange],
  );

  const [resolvedValue, setResolvedValue] = useControllableState<string | null>({
    defaultValue: defaultValue ?? null,
    onChange: handleValueChange,
    value,
  });

  const { labelProps, labelNode } = useComboBoxLabel({
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    label,
    placeholder,
  });

  const fixturesCSS: CSS | undefined =
    fixtures && controlVariant === 'neutral' ? getInteriorVariantStyles(fixtures) : undefined;

  const containerCSS = React.useMemo(
    () => merge(widthCSS(controlWidth), maxWidthCSS(controlMaxWidth), minWidthCSS(controlMinWidth)),
    [controlWidth, controlMaxWidth, controlMinWidth],
  );

  const triggerCSS = React.useMemo(() => merge({ width: '100%' }, fixturesCSS), [fixturesCSS]);

  const selectedRow = resolvedValue == null ? undefined : collection.byValue.get(resolvedValue);

  let selectedLabel: React.ReactNode;
  if (indeterminate) {
    selectedLabel = showPlaceholderForIndeterminate ? placeholder : <>&ndash;&nbsp;</>;
  } else if (selectedRow) {
    selectedLabel = selectedRow.label;
  } else {
    selectedLabel = placeholder;
  }

  const renderRow = React.useCallback(
    (row: ComboBoxRow) => (
      <SelectOption
        key={row.value}
        value={row.value}
        label={row.textValue}
        disabled={row.disabled || disabledValues.has(row.value)}
      >
        <ComboBoxListOptionBody>{row.label}</ComboBoxListOptionBody>
      </SelectOption>
    ),
    [disabledValues],
  );

  const renderGroup = React.useCallback(
    (group: ComboBoxRowGroup) => (
      <SelectGroup key={group.key}>
        {group.label != null && <SelectGroupLabel>{group.label}</SelectGroupLabel>}
        {group.items.map(renderRow)}
      </SelectGroup>
    ),
    [renderRow],
  );

  const listChildren = React.useMemo(
    () => (collection.groups ? collection.groups.map(renderGroup) : collection.rows.map(renderRow)),
    [collection.groups, collection.rows, renderGroup, renderRow],
  );

  const trigger = (
    <SelectTrigger
      {...remaining}
      {...labelProps}
      ref={ref}
      size={controlSize}
      appearance={controlGhost ? 'muted' : controlAppearance}
      variant={controlVariant}
      ghost={controlGhost}
      data-ghost={controlGhost || undefined}
      invalid={invalid ?? false}
      isDisabled={disabled}
      css={triggerCSS}
    >
      {icon && <SelectIcon icon={icon} />}
      <SelectValue size={controlSize}>{selectedLabel}</SelectValue>
      <SelectArrows icon="chevrons-vertical" />
    </SelectTrigger>
  );

  return (
    // The container wraps the root, not the other way around, so the hidden serialization input the
    // root appends after its children stays inside it — as a sibling it would steal `:last-child`
    // from the container, and `ControlGroup` squares a trailing Select's outer corners.
    <SelectContainer css={containerCSS}>
      <BaseSelect.Root<string>
        disabled={disabled}
        name={name}
        value={resolvedValue}
        onValueChange={setResolvedValue}
        onOpenChange={onOpenChange}
      >
        {labelNode}
        {selectedRow && canClearValue ? (
          <ControlGroup size={controlSize}>
            {trigger}
            <Button
              type="button"
              icon="cross"
              variant="secondary"
              arrangement="hidden-label"
              condense
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setResolvedValue(null);
              }}
            >
              Clear selection
            </Button>
          </ControlGroup>
        ) : (
          trigger
        )}
        <BaseSelect.Portal>
          <SelectPositioner
            alignItemWithTrigger={false}
            align="start"
            side="bottom"
            collisionPadding={8}
            sideOffset={4}
          >
            <SelectPopover>
              <SelectList {...labelProps}>{listChildren}</SelectList>
            </SelectPopover>
          </SelectPositioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
    </SelectContainer>
  );
}

export { ComboBoxItem as SelectItem, ComboBoxSection as SelectSection } from '../shared/ComboBox';
export type { SelectProps };
